
import { GoogleGenAI, Type } from "@google/genai";
import { Product, Order, CartItem } from '../types';

export const getProductSmartDescription = async (productName: string) => {
  try {
    const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: `Write a 2 sentence catchy e-commerce description for a product named "${productName}". Target gadget enthusiasts.`,
    });
    return response.text;
  } catch (error) {
    console.error("Gemini Error:", error);
    return null;
  }
};

export const generateVideoScript = async (product: Product, tone: string = 'energetic') => {
  try {
    const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: `Create a 30-second vertical video (Reels style) script for product: "${product.name}". 
      Description: ${product.description}. 
      Tone: ${tone}.
      
      Format as JSON:
      1. hook (The first 3 seconds to stop scrolling)
      2. body (3 key points to show)
      3. callToAction (What they should do now)
      4. visualInstructions (What should be on screen)`,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            hook: { type: Type.STRING },
            body: { type: Type.ARRAY, items: { type: Type.STRING } },
            callToAction: { type: Type.STRING },
            visualInstructions: { type: Type.STRING }
          },
          required: ["hook", "body", "callToAction", "visualInstructions"]
        }
      }
    });
    return JSON.parse(response.text);
  } catch (error) {
    console.error("Script Gen Error:", error);
    return null;
  }
};

export const generateLandingPageCopy = async (goal: string, products: Product[]) => {
  try {
    const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
    const productNames = products.map(p => p.name).join(", ");
    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: `Generate high-converting sales copy for a landing page. 
      Campaign Goal: ${goal}.
      Included Products: ${productNames}.
      
      Return as JSON with:
      1. heroHeadline (Impactful, 5-8 words)
      2. heroSubheadline (Compelling, 12-15 words)
      3. benefits (Array of 3 objects with {title, description})
      4. ctaText (Action-oriented, e.g., "Secure Yours Now")`,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            heroHeadline: { type: Type.STRING },
            heroSubheadline: { type: Type.STRING },
            benefits: {
              type: Type.ARRAY,
              items: {
                type: Type.OBJECT,
                properties: {
                  title: { type: Type.STRING },
                  description: { type: Type.STRING }
                },
                required: ["title", "description"]
              }
            },
            ctaText: { type: Type.STRING }
          },
          required: ["heroHeadline", "heroSubheadline", "benefits", "ctaText"]
        }
      }
    });
    return JSON.parse(response.text);
  } catch (error) {
    console.error("LP Gen Error:", error);
    return null;
  }
};

export const generateSeoMeta = async (productName: string, description: string) => {
  try {
    const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: `Generate SEO meta tags for a product: "${productName}". 
      Description: ${description}.
      Format as JSON: {"title": "...", "keywords": ["...", "..."], "metaDescription": "..."}`,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            title: { type: Type.STRING },
            keywords: { type: Type.ARRAY, items: { type: Type.STRING } },
            metaDescription: { type: Type.STRING }
          },
          required: ["title", "keywords", "metaDescription"]
        }
      }
    });
    return JSON.parse(response.text);
  } catch (error) {
    console.error("SEO Generation Error:", error);
    return null;
  }
};

export const getStoreAssistantReply = async (userMessage: string, products: Product[], storeName: string) => {
  try {
    const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
    const productContext = products.map(p => `- ${p.name}: $${p.price} (${p.description})`).join('\n');
    
    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: userMessage,
      config: {
        systemInstruction: `You are a helpful customer support agent for "${storeName}". 
        Here is the current inventory:
        ${productContext}
        
        Rules:
        1. Only suggest products available in the list above.
        2. Be polite, concise, and professional.
        3. If you don't know the answer, tell them a human agent will be with them soon.
        4. Do not mention that you are an AI unless asked.`
      }
    });
    return response.text;
  } catch (error) {
    console.error("Gemini Assistant Error:", error);
    return "I'm having a bit of trouble connecting to the store database. One of our human agents will help you shortly!";
  }
};

export const identifyProductFromImage = async (base64Image: string, inventory: Product[]) => {
  try {
    const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
    const productList = inventory.map(p => `${p.id}: ${p.name}`).join(', ');
    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: {
        parts: [
          {
            inlineData: {
              mimeType: "image/jpeg",
              data: base64Image
            }
          },
          {
            text: `Analyze this image. If it looks like a gadget or electronics, tell me which of these products from our inventory best matches it: [${productList}]. 
            Return ONLY the Product ID if a match is found, otherwise return 'NO_MATCH'.`
          }
        ]
      }
    });
    return response.text.trim();
  } catch (error) {
    console.error("Visual Search Error:", error);
    return 'NO_MATCH';
  }
};

export const getInventoryInsights = async (products: Product[], orders: Order[]) => {
  try {
    const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
    const inventoryData = products.map(p => `${p.name} (Stock: ${p.stock}, Price: $${p.price}, Wishlist: ${p.wishlistCount || 0})`).join('\n');
    const orderData = orders.map(o => `Order #${o.id} - Total: $${o.total} - Status: ${o.status}`).join('\n');

    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: `Analyze this store data and give 3 highly strategic business insights for the merchant. Pay special attention to high wishlist count vs low stock.
      
      Inventory & Demand:
      ${inventoryData}
      
      Recent Orders:
      ${orderData}
      
      Format the response as a JSON array of objects: [{"type": "warning" | "success" | "info", "title": "...", "advice": "..."}]`,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              type: { type: Type.STRING, description: "Type of insight" },
              title: { type: Type.STRING, description: "Short title" },
              advice: { type: Type.STRING, description: "Detailed advice" }
            },
            required: ["type", "title", "advice"]
          }
        }
      }
    });
    return JSON.parse(response.text);
  } catch (error) {
    console.error("AI Insight Error:", error);
    return [
      { type: "info", title: "Stable Operations", advice: "Your inventory levels look healthy for current demand." }
    ];
  }
};

export const getSmartCheckoutIncentive = async (cartItems: CartItem[], userCoins: number) => {
  try {
    const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
    const items = cartItems.map(i => `${i.product.name} (Qty: ${i.quantity}, Price: $${i.product.price})`).join(', ');
    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: `A customer has these items in their cart: [${items}]. They have ${userCoins} Lynq Coins.
      Write a highly persuasive, 1-sentence checkout incentive. 
      If they have many coins, suggest using them. If they have few, mention how many they will earn.
      The tone should be "Premium Tech Guru".`,
    });
    return response.text;
  } catch (error) {
    console.error("AI Incentive Error:", error);
    return "Complete your upgrade today and join the elite tech community.";
  }
};
